import { useState, useEffect, useCallback } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { useSession } from '../auth/session';
import {
  PageHeader, Card, Stat, Button, Pill, Field, Select,
  Table, THead, TH, TR, TD, Empty, fmtDate, fmtDT, InfoDot, SourceNote,
} from '../components/ui';
import { BillingEntityFilter, BillingEntityBadge } from '../components/BillingEntity';

// Field slug → the label shown in the audit trail.
const ADJ_LABEL = {
  billable: 'Billable', nonbillable: 'Non-Billable', expected: 'Expected Time',
  leave_days: 'Leave Days', overtime: 'Overtime',
};

// A right-aligned figure that becomes an input on click (when editable).
// Shows an amber dot + tooltip when the value is a manual override.
function EditableCell({ value, adj, editable, fmt, zeroDash, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => { setVal(value); }, [value]);
  const shown = zeroDash && Number(value) === 0 && !adj ? '—' : (fmt ? fmt(value) : value);
  if (!editable) return <span className={adj ? 'text-amber-400' : ''}>{shown}{adj ? ' •' : ''}</span>;
  if (editing) {
    const commit = () => { setEditing(false); onSave(val); };
    return (
      <input
        autoFocus type="number" step="0.25" value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setEditing(false); setVal(value); }
        }}
        className="w-16 bg-slate-900 border border-brand-500 rounded px-1 py-0.5 text-right text-slate-100 text-sm focus:outline-none"
      />
    );
  }
  return (
    <button
      type="button" onClick={() => setEditing(true)}
      title={adj ? `Manually adjusted from ${fmt ? fmt(adj.computed) : adj.computed}${adj.by ? ` · by ${adj.by}` : ''}` : 'Click to edit'}
      className={`underline decoration-dotted decoration-slate-600 underline-offset-2 hover:decoration-brand-500 ${adj ? 'text-amber-400' : 'text-slate-200'}`}
    >
      {shown}{adj ? ' •' : ''}
    </button>
  );
}

const RECON_SOURCES = [
  { source: 'Magnetic', label: 'Logged hours', detail: 'daily time entries per person/project (db_time_entries, from /users/{id}/time_entries)' },
  { source: 'Magnetic', label: 'Billable flag', detail: "each entry's task-level billable flag in Magnetic (a non-billable task inside a billable project still counts as non-billable)" },
  { source: 'Magnetic', label: 'Daily capacity', detail: 'basic hour capacity per person (part-timers come through lower than 8h)' },
  { source: 'BambooHR', label: 'Leave & public holidays', detail: 'approved time-off + holiday calendar — reduces expected capacity that day' },
  { source: 'App', label: 'Overtime rate (R/hr)', detail: 'finance-entered per person (Rates tab) — NOT salary/CTC' },
  { source: 'Derived', label: 'Overtime', detail: 'max(0, logged − capacity) per day; capacity 0 on weekends/holidays/full-leave' },
  { source: 'Derived', label: 'Amount', detail: 'overtime hours × rate × BCEA multiplier (weekday/Sat 1.5×, Sun/holiday 2×)' },
];

const H = (v) => `${Number(v || 0).toFixed(2)}h`;
const R = (v) => `R${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// 'YYYY-Www' → its workweek date range (Mon–Fri), e.g. '1 Jun – 5 Jun 2026'.
// The option value stays the ISO-week key; only the visible label changes.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const weekLabel = (key) => {
  const m = /^(\d{4})-W(\d{2})$/.exec(key || '');
  if (!m) return key;
  const [year, week] = [Number(m[1]), Number(m[2])];
  // ISO week 1 is the week containing Jan 4th; step back to that week's Monday.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (week - 1) * 7);
  const friday = new Date(monday);
  friday.setUTCDate(monday.getUTCDate() + 4);
  const d = (dt) => `${dt.getUTCDate()} ${MONTHS[dt.getUTCMonth()]}`;
  const label = `${d(monday)} – ${d(friday)} ${friday.getUTCFullYear()}`;
  // Weeks straddling a month boundary belong to neither month alone —
  // call it out so weekly totals aren't expected to sum to the month.
  return monday.getUTCMonth() === friday.getUTCMonth()
    ? label
    : `${label} · spans ${MONTHS[monday.getUTCMonth()]}/${MONTHS[friday.getUTCMonth()]}`;
};
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
// Render leave the way Fi writes it: consecutive same-type days collapse to a
// range and the " Leave" suffix is dropped — e.g. ['3','4','5' Sick, '15' Annual]
// → ['3-5 June (Sick)', '15 June (Annual)']. Returns an array of lines.
const formatLeaveRuns = (dates, types) => {
  if (!dates?.length) return [];
  const runs = [];
  dates.forEach((iso, i) => {
    const d = new Date(`${iso}T00:00:00Z`);
    const type = (types?.[i] || '').replace(/\s*leave$/i, '').trim();
    const last = runs[runs.length - 1];
    if (last && last.type === type && (d - last.end) / 86400000 === 1) last.end = d;
    else runs.push({ start: d, end: d, type });
  });
  const dm = (dt) => `${dt.getUTCDate()} ${MONTHS_FULL[dt.getUTCMonth()]}`;
  return runs.map((r) => {
    const range = r.start.getTime() === r.end.getTime()
      ? dm(r.start)
      : `${r.start.getUTCDate()}-${dm(r.end)}`;
    return r.type ? `${range} (${r.type})` : range;
  });
};
const inputCls = 'w-full bg-slate-900/60 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 focus:border-brand-500 focus:outline-none disabled:opacity-60 disabled:cursor-default';

export default function Reconciliation() {
  const { user } = useSession();
  const canEdit = user?.capabilities?.has('edit_overtime');
  const canEditTs = user?.capabilities?.has('edit_timesheet');

  const [gran, setGran] = useState('month');        // 'month' | 'week'
  const [period, setPeriod] = useState('');
  const [periods, setPeriods] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [overtime, setOvertime] = useState([]);
  const [rates, setRates] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [tab, setTab] = useState('users');
  const [entity, setEntity] = useState('');   // billing entity id ('' = all)
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sync, setSync] = useState(null);      // { last_sync, running, last_run }
  const [syncing, setSyncing] = useState(false);

  const isWeek = gran === 'week';

  // period options follow the granularity toggle
  useEffect(() => {
    api.recon.periods(gran).then((d) => {
      setPeriods(d);
      setPeriod(d[0] || '');
    }).catch(() => {});
  }, [gran]);

  const load = useCallback(() => {
    if (!period) return;
    const ent = entity ? Number(entity) : undefined;
    api.recon.summary(period).then(setSummary).catch(() => {});
    api.recon.users(period).then(setUsers).catch(() => {});
    api.recon.projects(period, ent).then(setProjects).catch(() => {});
    api.recon.payroll(period).then(setPayroll).catch(() => {});
    api.recon.exceptions(period).then(setExceptions).catch(() => {});
    api.recon.overtime(period, undefined, ent).then(setOvertime).catch(() => {});
    if (!isWeek) api.recon.adjustments(period).then(setAdjustments).catch(() => setAdjustments([]));
    else setAdjustments([]);
  }, [period, entity, isWeek]);
  useEffect(() => { load(); }, [load]);

  // ── Data-sync status (last sync, running, notify on completion) ──────
  const loadSync = useCallback(() => api.recon.syncStatus().then(setSync).catch(() => {}), []);
  useEffect(() => { loadSync(); }, [loadSync]);
  // While a sync is running, poll; when it finishes, notify and reload data.
  useEffect(() => {
    if (!sync?.running) { setSyncing(false); return undefined; }
    setSyncing(true);
    const t = setInterval(async () => {
      const s = await api.recon.syncStatus().catch(() => null);
      if (!s) return;
      setSync(s);
      if (!s.running) {
        clearInterval(t);
        const ok = s.last_run?.status === 'success';
        setMsg({ kind: ok ? 'ok' : 'err',
          text: ok ? 'Sync complete — data refreshed from Bamboo & Magnetic.'
                   : `Sync finished with errors — ${s.last_run?.error || 'see run detail'}.` });
        load();
      }
    }, 6000);
    return () => clearInterval(t);
  }, [sync?.running, load]);

  const runSync = async () => {
    setSyncing(true);
    setMsg({ kind: 'ok', text: 'Sync started — pulling Bamboo & Magnetic…' });
    try {
      const r = await api.recon.triggerSync();
      if (r.started === false) setMsg({ kind: 'ok', text: 'A sync is already running — watching for completion…' });
      loadSync();
    } catch (e) {
      setSyncing(false);
      setMsg({ kind: 'err', text: `Could not start sync: ${e.message}` });
    }
  };

  const relTime = (iso) => {
    if (!iso) return 'never';
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 90) return 'just now';
    if (s < 5400) return `${Math.round(s / 60)} min ago`;
    if (s < 129600) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  };
  const syncActive = syncing || sync?.running;

  // Persist a manual timesheet edit: optimistic local update (recompute
  // total + variance), POST live, then refresh the audit trail.
  const saveCell = async (row, field, newValue) => {
    const computed = row.adjustments?.[field]?.computed ?? row[field];
    const oldValue = row[field];
    if (Number(newValue) === Number(oldValue)) return;
    setUsers((rows) => rows.map((r) => {
      if (r.work_email !== row.work_email) return r;
      const next = { ...r, [field]: Number(newValue),
        adjustments: { ...(r.adjustments || {}), [field]: { computed, value: Number(newValue), by: user?.email, at: new Date().toISOString() } } };
      next.logged = Math.round((Number(next.billable) + Number(next.nonbillable)) * 100) / 100;
      next.variance = Math.round((Number(next.expected) - next.logged) * 100) / 100;
      return next;
    }));
    try {
      await api.recon.saveAdjustment(period, { work_email: row.work_email, field, new_value: Number(newValue), old_value: Number(oldValue) });
      api.recon.adjustments(period).then(setAdjustments).catch(() => {});
      setMsg({ kind: 'ok', text: `Saved ${row.full_name} · ${field}.` });
    } catch (e) {
      setMsg({ kind: 'err', text: `Save failed: ${e.message}` });
      load();
    }
  };
  useEffect(() => { if (canEdit) api.recon.rates().then(setRates).catch(() => {}); }, [canEdit]);

  const generate = async () => {
    setBusy(true); setMsg(null);
    try { await api.recon.generate(period); load(); setMsg({ kind: 'ok', text: 'Overtime candidates regenerated.' }); }
    catch (e) { setMsg({ kind: 'err', text: e.message }); }
    finally { setBusy(false); }
  };

  const patchRow = (id, patch) => {
    setOvertime((rows) => rows.map((r) => (r.overtime_id === id ? { ...r, ...patch } : r)));
    api.recon.updateOvertime(id, patch).catch((e) => { setMsg({ kind: 'err', text: e.message }); load(); });
  };
  const bulkApprove = async () => { await api.recon.bulkApprove(period); load(); };

  const varTone = (v) => (Math.abs(v) < 0.1 ? 'text-slate-400' : v < 0 ? 'text-brand-500' : 'text-amber-600');

  // overtime grouped by project (card shows client + hours + money)
  const byProject = {};
  overtime.forEach((o) => { (byProject[o.project_name] = byProject[o.project_name] || []).push(o); });

  const TABS = [
    ['users', 'Timesheets'],
    ['overtime', isWeek ? 'Overtime (review)' : 'Overtime queue'],
    ['projects', 'Billable split'],
    ['payroll', 'Payroll'],
    ['exceptions', `Exceptions (${exceptions.length})`],
    ...(canEdit ? [['rates', 'Rates']] : []),
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Timesheets & overtime"
        subtitle="Magnetic time vs BambooHR capacity & leave, laid out like Fi's monthly recon sheet. Overtime is detected per day. Weekly is a review view; monthly is the approve/pay unit. Identity is matched on work email."
        actions={
          <div className="flex items-end gap-3">
            <Field label="View">
              <Select value={gran} onChange={(e) => setGran(e.target.value)}>
                <option value="month">Monthly</option>
                <option value="week">Weekly</option>
              </Select>
            </Field>
            <Field label={isWeek ? 'Week' : 'Month'}>
              <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
                {periods.map((p) => <option key={p} value={p}>{isWeek ? weekLabel(p) : p}</option>)}
              </Select>
            </Field>
            <BillingEntityFilter value={entity} onChange={setEntity} />
            {canEdit && !isWeek && <Button variant="secondary" onClick={generate} loading={busy}><RefreshCw className="w-3.5 h-3.5" /> Regenerate</Button>}
            <div className="flex flex-col items-end justify-end gap-1">
              <span className="text-[11px] text-slate-500 whitespace-nowrap">
                Last sync: <span className={syncActive ? 'text-amber-500' : 'text-slate-300'}>{syncActive ? 'in progress…' : (sync ? relTime(sync.last_sync) : '…')}</span>
              </span>
              {canEdit && (
                <Button size="sm" variant="secondary" onClick={runSync} disabled={syncActive}>
                  <RefreshCw className={`w-3.5 h-3.5 ${syncActive ? 'animate-spin' : ''}`} />
                  {syncActive ? 'Syncing…' : 'Sync now'}
                </Button>
              )}
            </div>
          </div>
        }
      />

      <SourceNote items={RECON_SOURCES} />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Working days" value={summary.working_days}
            info="Weekdays in the period minus BambooHR public holidays. The denominator for expected hours." />
          <Stat label="Total time" value={H(summary.hours_logged)} sub={`of ${H(summary.hours_expected)} expected`}
            info="Σ Magnetic daily time entries (logged) = Billable + Non-billable. Expected = per-person daily capacity over working days, reduced by approved leave." />
          <Stat label="Billable time" value={H(summary.billable_hours)} sub={`${summary.billable_pct}% of logged`}
            info="Logged hours on billable tasks (each entry's own Magnetic task-level flag, at time of logging). % is of total logged." />
          <Stat label="Non-billable time" value={H(summary.nonbillable_hours)} sub="not client-facing"
            info="Logged hours on non-billable tasks (internal, admin, pre-sales) — per task, not per project." />
          <Stat label="Expected time" value={H(summary.hours_expected)} sub={`${summary.working_days} working days`}
            info="Σ per-person daily capacity (Magnetic basic hours) over working days, minus approved BambooHR leave/holidays." />
          <Stat label="Variance"
            value={<span className={varTone(summary.variance)}>{summary.variance > 0 ? '+' : ''}{H(summary.variance)}</span>}
            sub="expected − logged · red = overtime"
            info="Σ (Expected − Total) across everyone. Negative (red) = logged over capacity, i.e. likely overtime — matching Fi's 'Red = Overtime'." />
          <Stat label="Leave days" value={Number(summary.leave_days || 0).toFixed(2)} sub="approved (½-days = 0.5)"
            info="Σ approved BambooHR leave days across everyone in the period. Half-days count as 0.5." />
          <Stat label="Overtime" value={H(summary.overtime_hours)} sub={summary.overtime_amount ? R(summary.overtime_amount) : 'set rates'}
            info="Σ daily overtime = max(0, logged − capacity). Amount = hours × per-person rate × BCEA multiplier; shows once rates are set in the Rates tab." />
        </div>
      )}

      <nav className="flex gap-6 border-b border-slate-700 mb-6 flex-wrap">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`py-3 text-sm font-medium border-b-2 -mb-px ${tab === id ? 'border-brand-500 text-slate-100' : 'border-transparent text-slate-500 hover:text-slate-200'}`}>
            {label}
          </button>
        ))}
      </nav>

      {msg && <p className={`text-xs mb-4 ${msg.kind === 'ok' ? 'text-emerald-600' : 'text-brand-500'}`}>{msg.text}</p>}

      {tab === 'overtime' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-400">
              {overtime.length} overtime entries · {Object.keys(byProject).length} projects
              {isWeek && ' · review only — approve & pay from the monthly view'}
            </p>
            {!isWeek && (
              <div className="flex gap-2">
                {canEdit && <Button size="sm" variant="secondary" onClick={bulkApprove}>Approve all pending</Button>}
                <a href={api.recon.exportUrl(period)} className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 hover:bg-slate-700">
                  <Download className="w-3.5 h-3.5" /> Export approved
                </a>
              </div>
            )}
          </div>
          {Object.keys(byProject).length === 0 ? (
            <Card><Empty>No overtime. {canEdit && !isWeek && 'Hit Regenerate to recompute candidates.'}</Empty></Card>
          ) : Object.entries(byProject).map(([proj, rows]) => (
            <Card key={proj} title={proj}
              subtitle={
                <span className="inline-flex items-center gap-2">
                  {rows[0]?.billing_entity_name && <BillingEntityBadge name={rows[0].billing_entity_name} />}
                  <span>{`${rows[0]?.client_name || '—'} · ${rows.reduce((s, r) => s + Number(r.hours), 0).toFixed(2)}h${rows.some((r) => r.amount) ? ` · ${R(rows.reduce((s, r) => s + Number(r.amount || 0), 0))}` : ''}`}</span>
                </span>
              }
              padding="p-0">
              <Table>
                <THead>
                  <TH>Person</TH><TH>Date</TH><TH>Day</TH>
                  <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Hours <InfoDot>Overtime hours that day = max(0, logged − capacity), split across the day's projects. From Magnetic entries.</InfoDot></span></TH>
                  <TH><span className="inline-flex items-center gap-1">Reason <InfoDot>Why it counts: over_capacity (&gt;8h weekday), weekend, public_holiday, or on_leave. Drives the BCEA multiplier.</InfoDot></span></TH>
                  <TH><span className="inline-flex items-center gap-1">Billable <InfoDot>Whether the underlying Magnetic project is billable. Distinct from "Billed" (whether you charge the client for this overtime).</InfoDot></span></TH>
                  <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Rate <InfoDot>Per-person overtime R/hour from the Rates tab (finance-set, not CTC).</InfoDot></span></TH>
                  <TH align="right"><span className="inline-flex items-center gap-1 justify-end">× <InfoDot>BCEA multiplier: weekday/Saturday 1.5×, Sunday/public holiday 2× (configurable).</InfoDot></span></TH>
                  <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Amount <InfoDot>hours × rate × multiplier.</InfoDot></span></TH>
                  <TH><span className="inline-flex items-center gap-1">Billed <InfoDot>Will the client be charged for this overtime? Finance decision — carried into the export.</InfoDot></span></TH>
                  <TH>Allocation</TH><TH>Notes</TH><TH>Status</TH>
                </THead>
                <tbody>
                  {rows.map((o, i) => (
                    <OvertimeRow key={o.overtime_id || `${o.work_email}-${o.entry_date}-${i}`}
                      o={o} canEdit={canEdit && o.editable !== false} onChange={patchRow} />
                  ))}
                </tbody>
              </Table>
            </Card>
          ))}
        </div>
      )}

      {tab === 'users' && (() => {
        const tot = users.reduce((a, u) => ({
          billable:    a.billable    + (Number(u.billable)    || 0),
          nonbillable: a.nonbillable + (Number(u.nonbillable) || 0),
          logged:      a.logged      + (Number(u.logged)      || 0),
          expected:    a.expected    + (Number(u.expected)    || 0),
          variance:    a.variance    + (Number(u.variance)    || 0),
          leave_days:  a.leave_days  + (Number(u.leave_days)  || 0),
          overtime:    a.overtime    + (Number(u.overtime)    || 0),
        }), { billable: 0, nonbillable: 0, logged: 0, expected: 0, variance: 0, leave_days: 0, overtime: 0 });
        return (
        <>
        <Card padding="p-0">
          {/* Column order + labels mirror Fi's monthly "Recon" tab so it reads
              the same as her spreadsheet: User · Billable · Non-Billable ·
              Total · Expected · Variance · Leave Days · Leave Dates · Overtime.
              Cells with a dotted underline are editable (monthly view) — edits
              save live and are audited in the section below. */}
          {canEditTs && !isWeek && (
            <p className="px-4 pt-3 text-xs text-slate-500">Underlined figures are editable — click to adjust; changes save immediately and are logged below.</p>
          )}
          <Table maxHeight="max-h-[70vh]"
            className="[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20 [&_thead_th]:bg-slate-800 [&_thead_th]:whitespace-nowrap">
            <THead>
              <TH>User</TH>
              <TH align="right">Billable</TH>
              <TH align="right">Non-Billable</TH>
              <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Total Time Logged <InfoDot>Billable + Non-Billable. Recomputes when you edit either.</InfoDot></span></TH>
              <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Expected Time <InfoDot>Daily capacity (Magnetic basic hours) over working days, minus BambooHR leave/holidays.</InfoDot></span></TH>
              <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Time Variance <InfoDot>Expected − Total. Red (negative) = over-capacity, i.e. likely overtime — matching Fi's "Red = Overtime".</InfoDot></span></TH>
              <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Leave Days <InfoDot>Approved leave from BambooHR (half-days count as 0.5). Editable.</InfoDot></span></TH>
              <TH><span className="inline-flex items-center gap-1">Leave Dates <InfoDot>The day(s) of approved leave taken, with type — from BambooHR. Consecutive days show as a range, matching Fi's sheet.</InfoDot></span></TH>
              <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Overtime <InfoDot>Hours over capacity. Editable here; the Overtime queue tab has per-day/project detail + rates.</InfoDot></span></TH>
              <TH><span className="inline-flex items-center gap-1">Added to Payroll <InfoDot>Whether this person's overtime for the month has been approved and carried into payroll (Overtime queue). Monthly only.</InfoDot></span></TH>
            </THead>
            <tbody>
              {/* Totals row — pinned just under the sticky header so it stays
                  visible while scrolling the list (mirrors Fi's Total line). */}
              <tr className="text-slate-100 font-semibold [&>td]:sticky [&>td]:top-[37px] [&>td]:z-10 [&>td]:bg-slate-800 [&>td]:border-y [&>td]:border-slate-600">
                <TD className="!text-slate-100 font-semibold">Total · {users.length}</TD>
                <TD align="right" mono className="!text-slate-100 font-semibold">{H(tot.billable)}</TD>
                <TD align="right" mono className="!text-slate-100 font-semibold">{H(tot.nonbillable)}</TD>
                <TD align="right" mono className="!text-slate-100 font-semibold">{H(tot.logged)}</TD>
                <TD align="right" mono className="!text-slate-100 font-semibold">{H(tot.expected)}</TD>
                <TD align="right" mono className={`font-semibold ${varTone(tot.variance)}`}>{tot.variance > 0 ? '+' : ''}{H(tot.variance)}</TD>
                <TD align="right" mono className="!text-slate-100 font-semibold">{tot.leave_days.toFixed(2)}</TD>
                <TD />
                <TD align="right" mono className="!text-slate-100 font-semibold">{H(tot.overtime)}</TD>
                <TD />
              </tr>
              {users.map((u) => {
                const editable = canEditTs && !isWeek;
                return (
                <TR key={u.work_email}>
                  <TD>{u.full_name}</TD>
                  <TD align="right" mono><EditableCell value={u.billable} adj={u.adjustments?.billable} editable={editable} fmt={H} onSave={(v) => saveCell(u, 'billable', v)} /></TD>
                  <TD align="right" mono muted><EditableCell value={u.nonbillable} adj={u.adjustments?.nonbillable} editable={editable} fmt={H} zeroDash onSave={(v) => saveCell(u, 'nonbillable', v)} /></TD>
                  <TD align="right" mono>{H(u.logged)}</TD>
                  <TD align="right" mono muted><EditableCell value={u.expected} adj={u.adjustments?.expected} editable={editable} fmt={H} onSave={(v) => saveCell(u, 'expected', v)} /></TD>
                  <TD align="right" mono className={varTone(u.variance)}>{u.variance > 0 ? '+' : ''}{H(u.variance)}</TD>
                  <TD align="right" mono muted><EditableCell value={u.leave_days} adj={u.adjustments?.leave_days} editable={editable} fmt={(v) => Number(v).toFixed(2)} onSave={(v) => saveCell(u, 'leave_days', v)} /></TD>
                  <TD muted>{(() => {
                    const lines = formatLeaveRuns(u.leave_dates, u.leave_types);
                    return lines.length ? lines.map((l, i) => <div key={i}>{l}</div>) : '—';
                  })()}</TD>
                  <TD align="right" mono><EditableCell value={u.overtime} adj={u.adjustments?.overtime} editable={editable} fmt={H} zeroDash onSave={(v) => saveCell(u, 'overtime', v)} /></TD>
                  <TD>{u.added_to_payroll ? <Pill tone="ok">Added</Pill> : <span className="text-slate-600">—</span>}</TD>
                </TR>
              );})}
            </tbody>
          </Table>
        </Card>

        {/* Audit trail — history of manual adjustments, beneath the sheet */}
        <Card padding="p-0" title="Manual adjustments"
          subtitle={isWeek ? 'Adjustments are made on the monthly view.'
            : `${adjustments.length} change${adjustments.length === 1 ? '' : 's'} logged for this month`}
          className="mt-6">
          {isWeek ? (
            <div className="px-4 py-6"><Empty>Switch to the monthly view to make and see adjustments.</Empty></div>
          ) : adjustments.length === 0 ? (
            <div className="px-4 py-6"><Empty>No manual adjustments this month — every figure is straight from Magnetic &amp; BambooHR.</Empty></div>
          ) : (
            <Table>
              <THead>
                <TH>When</TH><TH>User</TH><TH>Field</TH>
                <TH align="right">From</TH><TH align="right">To</TH>
                <TH>By</TH><TH>Note</TH>
              </THead>
              <tbody>
                {adjustments.map((a, i) => (
                  <TR key={i}>
                    <TD muted>{fmtDT(a.adjusted_at)}</TD>
                    <TD>{a.full_name || a.work_email}</TD>
                    <TD><Pill tone="neutral">{ADJ_LABEL[a.field] || a.field}</Pill></TD>
                    <TD align="right" mono muted>{a.old_value == null ? '—' : H(a.old_value)}</TD>
                    <TD align="right" mono>{H(a.new_value)}</TD>
                    <TD muted>{a.adjusted_by}</TD>
                    <TD muted>{a.note || '—'}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        </>
        );
      })()}

      {tab === 'projects' && (
        <Card padding="p-0">
          {projects.length === 0 ? <Empty>No time logged this period.</Empty> : (
            <Table>
              <THead>
                <TH>Client</TH><TH>Project</TH><TH>Entity</TH><TH>Type</TH>
                <TH align="right">Billable</TH><TH align="right">Non-bill</TH><TH align="right">Total</TH>
              </THead>
              <tbody>
                {projects.map((p, i) => (
                  <TR key={i}>
                    <TD muted>{p.client_name}</TD>
                    <TD>{p.project_name}</TD>
                    <TD><BillingEntityBadge name={p.billing_entity_name} /></TD>
                    <TD><Pill tone={p.is_billable ? 'ok' : 'neutral'}>{p.is_billable ? 'Billable' : 'Non-bill'}</Pill></TD>
                    <TD align="right" mono>{H(p.billable)}</TD>
                    <TD align="right" mono muted>{p.nonbillable > 0 ? H(p.nonbillable) : '—'}</TD>
                    <TD align="right" mono>{H(p.total)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === 'payroll' && (
        <Card padding="p-0">
          {payroll.length === 0 ? <Empty>No overtime to reimburse this period.</Empty> : (
            <Table>
              <THead>
                <TH>Name</TH><TH align="right">OT hours</TH><TH align="right">Billable</TH>
                <TH align="right">Non-bill</TH><TH align="right">Amount</TH>
              </THead>
              <tbody>
                {payroll.map((p, i) => (
                  <TR key={i}>
                    <TD>{p.full_name}</TD>
                    <TD align="right" mono>{H(p.hours)}</TD>
                    <TD align="right" mono>{H(p.billable_hours)}</TD>
                    <TD align="right" mono muted>{p.nonbillable_hours > 0 ? H(p.nonbillable_hours) : '—'}</TD>
                    <TD align="right" mono>{p.amount ? R(p.amount) : '—'}</TD>
                  </TR>
                ))}
                <TR>
                  <TD><span className="font-semibold text-slate-200">Total</span></TD>
                  <TD align="right" mono>{H(payroll.reduce((s, p) => s + Number(p.hours), 0))}</TD>
                  <TD /><TD />
                  <TD align="right" mono><span className="font-semibold text-slate-200">{R(payroll.reduce((s, p) => s + Number(p.amount || 0), 0))}</span></TD>
                </TR>
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === 'exceptions' && (
        <Card padding="p-0">
          {exceptions.length === 0 ? <Empty>No exceptions.</Empty> : (
            <Table>
              <THead><TH>Severity</TH><TH>User</TH><TH>Issue</TH></THead>
              <tbody>
                {exceptions.map((e, i) => (
                  <TR key={i}>
                    <TD><Pill tone={e.severity === 'blocker' ? 'danger' : 'warn'}>{e.severity}</Pill></TD>
                    <TD>{e.user}</TD>
                    <TD muted>{e.issue}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === 'rates' && canEdit && (
        <RatesTab rates={rates} reload={() => api.recon.rates().then(setRates)} setMsg={setMsg} />
      )}
    </div>
  );
}

const STATUS_TONE = { approved: 'ok', rejected: 'danger', paid: 'ok', pending: 'neutral' };
const STATUSES = ['pending', 'approved', 'rejected', 'paid'];

// One overtime row. Money/billable columns are read-only (engine-derived);
// billed-to-client, allocation, notes, status are inline-editable and persist
// on change/blur. Weekly rows arrive with canEdit=false (review only).
function OvertimeRow({ o, canEdit, onChange }) {
  const [notes, setNotes] = useState(o.notes || '');
  const [alloc, setAlloc] = useState(o.allocation || '');
  // Re-sync local edits only when the row identity changes — deliberately not
  // on o.notes/o.allocation, which would clobber the user's in-progress input.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setNotes(o.notes || ''); setAlloc(o.allocation || ''); }, [o.overtime_id]);

  return (
    <TR>
      <TD>{o.full_name}</TD>
      <TD muted mono>{fmtDate(o.entry_date)}</TD>
      <TD muted>{o.weekday || ''}</TD>
      <TD align="right" mono>{Number(o.hours).toFixed(2)}</TD>
      <TD><Pill tone={o.reason === 'over_capacity' ? 'neutral' : 'warn'}>{o.reason.replace('_', ' ')}</Pill></TD>
      <TD><Pill tone={o.billable ? 'ok' : 'neutral'}>{o.billable ? 'Yes' : 'No'}</Pill></TD>
      <TD align="right" mono muted>{o.ot_rate ? Number(o.ot_rate).toFixed(0) : '—'}</TD>
      <TD align="right" mono muted>{o.multiplier ? `${Number(o.multiplier)}×` : ''}</TD>
      <TD align="right" mono>{o.amount ? `R${Number(o.amount).toFixed(0)}` : '—'}</TD>
      <TD>
        <input type="checkbox" disabled={!canEdit} checked={!!o.billed_to_client}
          onChange={(e) => onChange(o.overtime_id, { billed_to_client: e.target.checked })}
          className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-brand-500 focus:ring-brand-500" />
      </TD>
      <TD>
        <input value={alloc} disabled={!canEdit} placeholder="—"
          onChange={(e) => setAlloc(e.target.value)}
          onBlur={() => { if (alloc !== (o.allocation || '')) onChange(o.overtime_id, { allocation: alloc }); }}
          className={inputCls} />
      </TD>
      <TD>
        <input value={notes} disabled={!canEdit} placeholder="—"
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => { if (notes !== (o.notes || '')) onChange(o.overtime_id, { notes }); }}
          className={inputCls} />
      </TD>
      <TD>
        {canEdit ? (
          <select value={o.status} onChange={(e) => onChange(o.overtime_id, { status: e.target.value })} className={inputCls}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : <Pill tone={STATUS_TONE[o.status] || 'neutral'}>{o.status}</Pill>}
      </TD>
    </TR>
  );
}

// Per-person overtime R/hr (finance-maintained, not CTC). Drives the money
// columns in the queue + payroll roll-up once set.
function RatesTab({ rates, reload, setMsg }) {
  const save = (employeeId, value) => {
    const hourly_rate = Number(value) || 0;
    api.recon.setRate(employeeId, hourly_rate)
      .then(reload)
      .catch((e) => setMsg({ kind: 'err', text: e.message }));
  };
  return (
    <Card padding="p-0" title="Overtime rates" subtitle="Per-person R/hour. Reimbursement = rate × overtime hours × multiplier. Not derived from CTC.">
      <Table>
        <THead><TH>Name</TH><TH align="right">R / hour</TH><TH>Updated by</TH></THead>
        <tbody>
          {rates.map((r) => (
            <RateRow key={r.employee_id} r={r} onSave={save} />
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function RateRow({ r, onSave }) {
  const [val, setVal] = useState(r.hourly_rate ?? 0);
  useEffect(() => { setVal(r.hourly_rate ?? 0); }, [r.employee_id, r.hourly_rate]);
  return (
    <TR>
      <TD>{r.full_name}</TD>
      <TD align="right">
        <input type="number" min="0" step="1" value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => { if (Number(val) !== Number(r.hourly_rate)) onSave(r.employee_id, val); }}
          className={`${inputCls} text-right w-28 ml-auto`} />
      </TD>
      <TD muted>{r.updated_by || '—'}</TD>
    </TR>
  );
}
